import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IoImageOutline } from "react-icons/io5";
import useTypewriterPlaceholder from "../../hooks/useTypewriterPlaceholder";
import OptimizedImage from "../common/OptimizedImage";
import DesignPhotoModal from "./DesignPhotoModal";
import { trackCTAClicked, trackHeroEstimateStarted } from "../../utils/analytics";
import "../../styles/Home.css";

const TYPEWRITER_PHRASES = [
  "How much does a new kitchen cost?",
  "I need a plumber in Manchester",
  "What's the average cost of a loft conversion?",
  "How much to paint a 3-bed house?",
  "New boiler installation cost?",
  "How much does a bathroom renovation cost?",
  "Garden landscaping for a semi-detached",
  "Cost of a single storey extension?",
  "How much to replace a roof in 2026?",
  "Driveway resurfacing cost?",
];

const HeroSection = ({ onContractorClick }) => {
  const [query, setQuery] = useState("");
  const [designOpen, setDesignOpen] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const {
    displayText,
    isAnimating,
    onFocus: twOnFocus,
    onBlur: twOnBlur,
    setUserActive,
  } = useTypewriterPlaceholder(TYPEWRITER_PHRASES);

  useEffect(() => {
    if (query.length > 0) {
      setUserActive(true);
    }
  }, [query, setUserActive]);

  const handleInputFocus = () => {
    twOnFocus();
  };

  const handleInputBlur = () => {
    if (!query.trim()) {
      twOnBlur();
    }
  };

  const handleSubmit = () => {
    const trimmed = query.trim();
    trackHeroEstimateStarted({
      audience: "homeowner",
      source: "homepage_hero",
      query_present: trimmed ? "true" : "false",
    });
    if (!trimmed) {
      navigate("/chat?source=homepage_hero");
      return;
    }
    navigate(`/chat?q=${encodeURIComponent(trimmed)}&source=homepage_hero`);
  };

  const openDesign = () => {
    trackCTAClicked("homepage_hero", "design_popup");
    setDesignOpen(true);
  };

  const closeDesign = useCallback(() => setDesignOpen(false), []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="hero-container">
      <div className="hero-text">
        <h1 className="hero-title">
          Know what it costs <span className="highlight-text">before you call</span>
        </h1>

        <p className="hero-description">
          Describe your home improvement project in plain English. Percy asks
          follow-up questions and uses the details and measurements you confirm
          to build an indicative estimate for supported work. Use it to plan
          your budget, then request a written quote from a tradesperson.
        </p>

        <div className="estimator-input-container">
          <label htmlFor="hero-estimator-input" className="visually-hidden">
            Describe your home improvement project
          </label>
          <div className="estimator-input-row">
            <div className="estimator-input-wrapper">
              <input
                ref={inputRef}
                id="hero-estimator-input"
                name="project-description"
                type="text"
                className="estimator-input"
                placeholder=""
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                aria-label="Describe your home improvement project"
              />
              {isAnimating && !query && (
                <span className="estimator-typewriter-overlay" aria-hidden="true">
                  {displayText}
                  <span className="estimator-typewriter-cursor">|</span>
                </span>
              )}
              <button
                type="button"
                className="estimator-design-btn"
                onClick={openDesign}
                aria-label="Upload a photo and get a design"
                title="Upload a photo and get a design"
              >
                <IoImageOutline aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="estimator-submit-btn"
              onClick={handleSubmit}
              aria-label={
                query.trim()
                  ? "Get a cost estimate for your project"
                  : "Open chat assistant"
              }
            >
              Get Estimate
            </button>
          </div>
          <div className="estimator-trust-micro">
            <span>Free</span>
            <span className="estimator-trust-dot" aria-hidden="true" />
            <span>No signup</span>
            <span className="estimator-trust-dot" aria-hidden="true" />
            <span>Photo-aware</span>
            <span className="estimator-trust-dot" aria-hidden="true" />
            <span>Local trade matching</span>
          </div>
        </div>

        <div className="hero-cta-row">
          <Link
            to="/local-contractors?source=homepage_hero"
            className="button button-secondary hero-cta-btn"
            onClick={() => trackCTAClicked("homepage_hero", "local_contractors")}
          >
            Find Local Contractors
          </Link>
          <Link
            to="/for-tradespeople"
            className="button button-outline hero-cta-btn"
            onClick={
              onContractorClick
                ? (e) => {
                    e.preventDefault();
                    onContractorClick();
                  }
                : undefined
            }
          >
            Join as a Tradesperson
          </Link>
        </div>
      </div>

      <div className="hero-image">
        <OptimizedImage
          src="/images/WavingPenguin.jpg"
          alt="Pricing Penguin AI assistant"
          className="hero-image-content"
          width={400}
          height={400}
          webpSrcSet="/images/WavingPenguin-440.webp 440w, /images/WavingPenguin-640.webp 640w, /images/WavingPenguin.webp 1024w"
          sizes="(max-width: 767px) 300px, 440px"
          loading="eager"
          fetchpriority="high"
        />
      </div>

      <DesignPhotoModal open={designOpen} onClose={closeDesign} />
    </div>
  );
};

export default HeroSection;
